import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loading } from "@core/components";
import { useWorkspaceContext } from "@core/context";
import { useGetProject } from "../hooks";
import { ProjectContext } from "../context";

type Props = { children: React.ReactNode };

export function ProjectContextGuard({ children }: Props) {
  const { projectId } = useParams<{ projectId: string }>();
  const { workspaceId } = useWorkspaceContext();
  const navigate = useNavigate();

  const { data: project, isLoading, isError } = useGetProject(projectId ?? "");

  const contextValue = useMemo(
    () => ({
      project: project!,
      projectId: project?.id ?? "",
      role: project?.role ?? ("member" as const),
      rootFolderId: project?.folder_id ?? "",
    }),
    [project],
  );

  useEffect(() => {
    if (!projectId || isLoading || isError) return;
    if (!project || !project.is_active) {
      navigate(`/workspace/${workspaceId}/project-portal`, { replace: true });
    }
  }, [isLoading, isError, project, projectId, navigate, workspaceId]);

  if (!projectId || isLoading) return <Loading isLoading />;
  if (isError || !project || !project.is_active) return null;

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
}
